import React from 'react';
import styled, { css } from 'styled-components';
import { Mono } from '../../../../../generic/ui/Mono';
import { Tag } from '../../../../../generic/ui/Tag';

/**
 * Общие куски карточек модуля: их делят KavCard, CustomerCard и PalletsModal.
 * Размеры и токены — по knisot/Create/Components/KnisaTitleCard и
 * knisot/Components/KnisotRowItem, чтобы drivers не разъезжался с каноном.
 */

/** Айтем сплошного списка: своей рамки нет, разделитель снизу — паттерн Surface + CardRow из knisot. */
export const RowShell = styled.div<{ $isLast?: boolean }>`
    position: relative;
    width: 100%;
    background: ${({ theme }) => theme.colors.surface};
    border-bottom: 1px solid ${({ theme }) => theme.colors.line};

    ${({ $isLast }) =>
        $isLast &&
        css`
            border-bottom: 0;
        `}
`;

/** Карточка-контейнер (аналог KnisaTitleCard.Card). */
export const CardShell = styled.div`
    position: relative;
    width: 100%;
    border: 1px solid ${({ theme }) => theme.colors.line};
    border-radius: 1rem;
    background: ${({ theme }) => theme.colors.surface};
    overflow: hidden;
`;

/** Полоса чипов под основной строкой карточки. */
export const ChipStrip = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.375rem 0.5rem;
    padding: 0 1.25rem 0.5rem;
    direction: rtl;

    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
        padding: 0 0.5rem 0.5rem;
    }
`;

/** Полоса чипов, отделённая от зоны выше отступом (без линии) — карточки кава. */
export const DetachedChipStrip = styled(ChipStrip)`
    padding-top: 0.25rem;

    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
        padding-top: 0.125rem;
    }
`;

export const IconSlot = styled.span<{ $muted?: boolean }>`
    display: inline-flex;
    align-items: center;
    color: ${({ theme, $muted }) => ($muted ? theme.colors.ink3 : theme.colors.ink2)};
    flex-shrink: 0;
`;

export const IdText = styled(Mono)`
    font-size: ${({ theme }) => theme.text.lg};
    font-weight: 600;
    color: ${({ theme }) => theme.colors.ink};

    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
        font-size: ${({ theme }) => theme.textMobile.lg};
    }
`;

export const Reference = styled(Mono)`
    font-size: ${({ theme }) => theme.text.sm};
    font-weight: 400;
    color: ${({ theme }) => theme.colors.ink4};

    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
        font-size: ${({ theme }) => theme.textMobile.sm};
    }
`;

export const PrimaryName = styled.span`
    font-family: ${({ theme }) => theme.fonts.sans};
    font-size: ${({ theme }) => theme.text.lg};
    font-weight: 500;
    color: ${({ theme }) => theme.colors.ink};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100%;

    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
        font-size: ${({ theme }) => theme.textMobile.lg};
    }
`;

export const SecondaryText = styled.span`
    font-family: ${({ theme }) => theme.fonts.sans};
    font-size: ${({ theme }) => theme.text.sm};
    color: ${({ theme }) => theme.colors.ink3};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;

    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
        font-size: ${({ theme }) => theme.textMobile.sm};
    }
`;

export const TimeText = styled(Mono)`
    font-size: ${({ theme }) => theme.text.sm};
    color: ${({ theme }) => theme.colors.ink3};
    white-space: nowrap;

    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
        font-size: ${({ theme }) => theme.textMobile.sm};
    }
`;

/** Акцентная кнопка действия — паттерн KnisotRowItem.PrintBtn. */
export const ActionBtn = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.25rem;
    height: 2rem;
    min-width: 2rem;
    padding: 0 0.5rem;
    border-radius: 0.5rem;
    background: ${({ theme }) => theme.colors.accent};
    color: ${({ theme }) => theme.colors.ink};
    border: 1px solid ${({ theme }) => theme.colors.accent};
    font-family: ${({ theme }) => theme.fonts.sans};
    font-size: ${({ theme }) => theme.text.md};
    font-weight: 600;
    white-space: nowrap;
    cursor: pointer;
    user-select: none;
    transition: all ${({ theme }) => theme.motion.fast} ease;

    svg {
        width: 1rem;
        height: 1rem;
    }

    &:hover {
        background: ${({ theme }) => theme.colors.accentDeep};
        border-color: ${({ theme }) => theme.colors.accentDeep};
    }

    &:active {
        transform: translateY(1px);
    }
`;

/**
 * Нейтральная иконочная кнопка — паттерн KnisaTitleCard.EditBtn.
 * `$active` — действие выполнено (акцент), `$danger` — ещё не выполнено и это
 * требует внимания. Оба опциональны, без них кнопка нейтральная как раньше.
 */
export const NeutralBtn = styled.button<{ $active?: boolean; $danger?: boolean }>`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 2rem;
    height: 2rem;
    padding: 0;
    flex-shrink: 0;
    border-radius: 0.5rem;
    border: 1px solid
        ${({ theme, $active, $danger }) =>
            $active ? theme.colors.accent : $danger ? theme.colors.danger : theme.colors.line};
    background: ${({ theme, $active, $danger }) =>
        $active ? theme.colors.accent : $danger ? theme.colors.dangerSoft : theme.colors.line2};
    color: ${({ theme, $active, $danger }) =>
        $active ? theme.colors.ink : $danger ? theme.colors.danger : theme.colors.ink3};
    cursor: pointer;
    transition: all ${({ theme }) => theme.motion.fast} ease;

    svg {
        width: 1rem;
        height: 1rem;
    }

    &:hover {
        background: ${({ theme }) => theme.colors.accent};
        color: ${({ theme }) => theme.colors.ink};
        border-color: ${({ theme }) => theme.colors.accent};
    }

    &:active {
        transform: translateY(1px);
    }
`;

export const Actions = styled.div`
    display: flex;
    align-items: center;
    gap: 0.375rem;
    flex-shrink: 0;
`;

/** Тег в строке с текстом: не сжимается, иначе его плющит раньше соседнего текста. */
export const DriversTag = styled(Tag)`
    flex-shrink: 0;
`;

/** Действия офиса в полосе чипов: прижаты к концу — в RTL это дальний край, под временем и кнопкой начала работы. */
export const OfficeActions = styled(Actions)`
    margin-inline-start: auto;
`;

const ChipRoot = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 0.375rem;
    padding: 0.25rem 0.5rem;
    border-radius: ${({ theme }) => theme.radii.pill};
    background: ${({ theme }) => theme.colors.line2};
    color: ${({ theme }) => theme.colors.ink2};
    font-family: ${({ theme }) => theme.fonts.sans};
    font-size: ${({ theme }) => theme.text.sm};
    line-height: 1.2;

    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
        font-size: ${({ theme }) => theme.textMobile.sm};
    }
`;

const ChipIcon = styled.span`
    display: inline-flex;
    align-items: center;
    color: ${({ theme }) => theme.colors.ink3};

    svg {
        width: 0.875rem;
        height: 0.875rem;
    }
`;

const ChipValue = styled(Mono)`
    font-feature-settings: 'tnum' 1;
`;

const ChipUnit = styled.span`
    color: ${({ theme }) => theme.colors.ink4};
    font-size: ${({ theme }) => theme.text.xs};

    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
        font-size: ${({ theme }) => theme.textMobile.xs};
    }
`;

export interface MetricChipProps {
    icon: React.ReactNode;
    value: React.ReactNode;
    unit?: string;
    label?: string;
}

/** Чип метрики: иконка → число → единица. */
export const MetricChip: React.FC<MetricChipProps> = ({ icon, value, unit, label }) => (
    <ChipRoot>
        <ChipIcon>{icon}</ChipIcon>
        <ChipValue>{value}</ChipValue>
        {unit && <ChipUnit>{unit}</ChipUnit>}
        {label && <ChipUnit>{label}</ChipUnit>}
    </ChipRoot>
);
